/**
 * API service for Trade Modification endpoints (KAN-134).
 */

import { apiClient, APIResponse } from './client';
import type {
  ModificationTrade,
  ModificationHistoryEntry,
  InitiateModificationPayload,
  InitiateModificationResult,
  ConfirmModificationPayload,
  ConfirmModificationResult,
  RejectModificationPayload,
  RejectModificationResult,
  RegenerateAllocationsResult,
  ModificationConfig,
  ApplyChildGroupingsPayload,
  ApplyChildGroupingsResult,
} from '../../types/modification';

/** Build ?entity_id=X query string when entityId is provided. */
function entityParam(entityId?: string): string {
  return entityId ? `?entity_id=${entityId}` : '';
}

class ModificationService {
  private basePath = '/api/v1/modifications';

  /**
   * Get trades eligible for modification (Confirmed / ContractSigned).
   */
  async getEligibleTrades(
    clientId: string,
    entityIds?: string[],
    counterpartyId?: string,
    messageId?: string,
    sourceBtn?: string,
  ): Promise<APIResponse<ModificationTrade[]>> {
    const queryParams: string[] = [];
    if (entityIds?.length) queryParams.push(`entity_ids=${entityIds.join(',')}`);
    if (counterpartyId) queryParams.push(`counterparty_id=${counterpartyId}`);
    if (messageId) queryParams.push(`message_id=${messageId}`);
    if (sourceBtn) queryParams.push(`source_btn=${encodeURIComponent(sourceBtn)}`);
    const params = queryParams.length ? `?${queryParams.join('&')}` : '';
    return apiClient.get<ModificationTrade[]>(
      `${this.basePath}/${clientId}/eligible-trades${params}`
    );
  }

  /**
   * Get trades with pending modification requests.
   */
  async getPendingModifications(
    clientId: string,
    entityIds?: string[]
  ): Promise<APIResponse<ModificationTrade[]>> {
    const params = entityIds?.length
      ? `?entity_ids=${entityIds.join(',')}`
      : '';
    return apiClient.get<ModificationTrade[]>(
      `${this.basePath}/${clientId}/pending${params}`
    );
  }

  /**
   * Get completed and rejected modifications (history).
   */
  async getModificationHistory(
    clientId: string,
    entityIds?: string[]
  ): Promise<APIResponse<ModificationHistoryEntry[]>> {
    const params = entityIds?.length
      ? `?entity_ids=${entityIds.join(',')}`
      : '';
    return apiClient.get<ModificationHistoryEntry[]>(
      `${this.basePath}/${clientId}/history${params}`
    );
  }

  /**
   * Initiate a modification request on an eligible trade (BO / Admin).
   */
  async initiateModification(
    clientId: string,
    tradeId: string,
    payload: InitiateModificationPayload,
    entityId?: string
  ): Promise<APIResponse<InitiateModificationResult>> {
    return apiClient.post<InitiateModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/initiate${entityParam(entityId)}`,
      payload
    );
  }

  /**
   * Confirm a pending modification (FO / Admin). Executes Cancel + New.
   */
  async confirmModification(
    clientId: string,
    tradeId: string,
    payload: ConfirmModificationPayload,
    entityId?: string
  ): Promise<APIResponse<ConfirmModificationResult>> {
    return apiClient.post<ConfirmModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/confirm${entityParam(entityId)}`,
      payload
    );
  }

  /**
   * Reject a pending modification request (FO / Admin).
   */
  async rejectModification(
    clientId: string,
    tradeId: string,
    payload: RejectModificationPayload,
    entityId?: string
  ): Promise<APIResponse<RejectModificationResult>> {
    return apiClient.post<RejectModificationResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/reject${entityParam(entityId)}`,
      payload
    );
  }

  /**
   * Regenerate allocation child trades from preserved config.
   */
  async regenerateAllocations(
    clientId: string,
    tradeId: string,
    entityId?: string
  ): Promise<APIResponse<RegenerateAllocationsResult>> {
    return apiClient.post<RegenerateAllocationsResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/regenerate-allocations${entityParam(entityId)}`
    );
  }

  /**
   * Get the modification config for this client (Cancel+New vs edit-in-place per type).
   */
  async getModificationConfig(
    clientId: string
  ): Promise<APIResponse<ModificationConfig>> {
    return apiClient.get<ModificationConfig>(
      `${this.basePath}/config/${clientId}`
    );
  }

  /**
   * Save the modification config for this client (admin only).
   */
  async saveModificationConfig(
    clientId: string,
    config: ModificationConfig
  ): Promise<APIResponse<ModificationConfig>> {
    return apiClient.put<ModificationConfig>(
      `${this.basePath}/config/${clientId}`,
      config
    );
  }

  /**
   * Apply FO-confirmed grouping changes to child trades after a GroupingChange.
   */
  async applyChildGroupings(
    clientId: string,
    tradeId: string,
    payload: ApplyChildGroupingsPayload,
    entityId?: string
  ): Promise<APIResponse<ApplyChildGroupingsResult>> {
    return apiClient.post<ApplyChildGroupingsResult>(
      `${this.basePath}/${clientId}/trades/${tradeId}/apply-child-groupings${entityParam(entityId)}`,
      payload
    );
  }

  /**
   * Cancel an approved modification awaiting counterparty confirmation.
   * Reverts the trade from PendingModification to its pre-modification state.
   */
  async cancelPendingModification(
    clientId: string,
    tradeId: string,
    entityId?: string
  ): Promise<APIResponse<any>> {
    return apiClient.post<any>(
      `${this.basePath}/${clientId}/trades/${tradeId}/cancel-pending-modification${entityParam(entityId)}`
    );
  }

  /**
   * Manually confirm a modification without a counterparty email.
   * Moves the trade from PendingModification to Confirmed.
   */
  async confirmWithoutCounterparty(
    clientId: string,
    tradeId: string,
    entityId?: string
  ): Promise<APIResponse<any>> {
    return apiClient.post<any>(
      `${this.basePath}/${clientId}/trades/${tradeId}/confirm-without-counterparty${entityParam(entityId)}`
    );
  }
}

export const modificationService = new ModificationService();
